import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Collapse, Divider,
  FormControl, FormControlLabel, IconButton, InputLabel, MenuItem, Paper,
  Select, Stack, Switch, TextField, Typography, alpha, useTheme,
} from '@mui/material';
import {
  Add as AddIcon, ArrowBack as BackIcon, Delete as DeleteIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useVenue } from '../../../../context/VenueContext';
import menuItemService from '../../../../services/menuItemService';
import menuSectionService from '../../../../services/menuSectionService';
import menuFormatService from '../../../../services/menuFormatService';
import menuItemFormatService from '../../../../services/menuItemFormatService';
import inventoryItemService from '../../../../services/inventoryItemService';
import type { InventoryItemWithBalance } from '../../../../services/inventoryItemService';
import WineSearchBox from '../../../../components/venue/WineSearchBox';
import type { WineSearchResult } from '../../../../components/venue/WineSearchBox';
import CreateWineModal from '../../../../components/venue/CreateWineModal';
import FormatExecutionBlock from '../../../../components/venue/FormatExecutionBlock';
import ItemFulfillmentPanel from '../../../../components/venue/ItemFulfillmentPanel';
import type { FulfillmentMode } from '../../../../components/venue/ItemFulfillmentPanel';
import type { Wine } from '../../../../services/wineService';
import type {
  MenuFormat, MenuItemFormat as MenuItemFormatDoc, MenuItemKind, MenuSection,
  MenuVisibility, MenuWineDetails, MenuWineScoreSource,
} from '../../../../types/menu';

/**
 * Generic MenuItem create / edit form (Phase G.3).
 *
 * This is the single item form for every supported kind. It replaces the former
 * MenuItemFormWine + MenuItemFormNonWine split — the two shared one spine
 * (displaySnapshot, serving-size rows + per-serving Stock & recipe, the
 * diff-save of MenuItemFormat documents) and differed only in the optional
 * wine-identity fields and the kind/wineDetails payload.
 *
 * API compatibility is exact (Phase G.3 is frontend-only, no backend change):
 *   · When a wine identity is linked, the payload is byte-compatible with the
 *     old wine form: kind = 'WINE', wineDetails set, displaySnapshot derived
 *     from the wine (name + producer · region).
 *   · When no wine is linked, the payload is byte-compatible with the old
 *     non-wine form: kind = the intended non-wine kind, no wineDetails,
 *     displaySnapshot from the typed name/subtitle.
 *   · `kind` is never sent on update (the backend's KIND_CHANGE_NOT_ALLOWED
 *     guard stays untriggered; kind is immutable post-create).
 *
 * Architecture note (Phase G — generic primitive): "wine" is an OPTIONAL linked
 * identity helper here, not a separate item architecture. `kind` is carried only
 * because the backend still requires it; it is a compatibility field pending the
 * Jeroen-gated backend collapse, not the long-term model.
 */

const SCORE_SOURCES: MenuWineScoreSource[] = ['JS', 'WA', 'WS', 'AV', 'RP'];
const SCORE_SOURCE_LABELS: Record<MenuWineScoreSource, string> = {
  JS: 'James Suckling',
  WA: 'Wine Advocate',
  WS: 'Wine Spectator',
  AV: 'Antonio Galloni / Vinous',
  RP: 'Robert Parker',
};

interface KindLabels {
  itemLabel: string;
  itemNameLabel: string;
  placeholder: string;
  detailsTitle: string;
}

// Copy per intended kind. WINE is included for the title/noun only — its manual
// name fields are never rendered (the display name derives from the wine).
const KIND_LABELS: Record<MenuItemKind, KindLabels> = {
  WINE: { itemLabel: 'Wine', itemNameLabel: 'Wine name', placeholder: '', detailsTitle: 'Wine details' },
  COCKTAIL: { itemLabel: 'Cocktail', itemNameLabel: 'Cocktail name', placeholder: 'e.g. Margarita', detailsTitle: 'Cocktail details' },
  DISH: { itemLabel: 'Dish', itemNameLabel: 'Dish name', placeholder: 'e.g. Grilled chicken', detailsTitle: 'Dish details' },
  BEER: { itemLabel: 'Beer', itemNameLabel: 'Beer name', placeholder: 'e.g. Pilsner Urquell', detailsTitle: 'Beer details' },
  SPIRIT: { itemLabel: 'Spirit', itemNameLabel: 'Spirit name', placeholder: 'e.g. Talisker 10', detailsTitle: 'Spirit details' },
  RETAIL: { itemLabel: 'Item', itemNameLabel: 'Item name', placeholder: 'e.g. Gift box', detailsTitle: 'Retail item details' },
  OTHER: { itemLabel: 'Item', itemNameLabel: 'Item name', placeholder: 'e.g. Item name', detailsTitle: 'Item details' },
  TASTING_MENU: { itemLabel: 'Item', itemNameLabel: 'Item name', placeholder: '', detailsTitle: 'Item details' },
};

interface ScoreRow {
  source: MenuWineScoreSource | '';
  score: string;
}

interface FormData {
  name: string;
  subtitle: string;
  basePrice: string;
  currency: string;
  sectionId: string;
  visibility: MenuVisibility;
  notes: string;
  externalUrl: string;
  needsReview: boolean;
  position: string;
  bottleSize: string;
  binNumber: string;
}

interface FormatRow {
  /** Server id when persisted, undefined when this row is new. */
  _id?: string;
  formatId: string;
  /** Free-text price to support typing in progress. Validated on save. */
  price: string;
  visibility: MenuVisibility;
  inventoryItem: InventoryItemWithBalance | null;
}

const emptyFormData = (defaultCurrency: string): FormData => ({
  name: '',
  subtitle: '',
  basePrice: '',
  currency: defaultCurrency,
  sectionId: '',
  visibility: 'LIVE',
  notes: '',
  externalUrl: '',
  needsReview: false,
  position: '',
  bottleSize: '',
  binNumber: '',
});

/**
 * Map a freshly-created Wine (from CreateWineModal) to the lightweight
 * WineSearchResult shape that WineSearchBox / this form consume.
 */
const wineToSearchResult = (wine: Wine): WineSearchResult => ({
  _id: wine._id,
  name: wine.name,
  displayVintage: wine.displayVintage,
  vintageType: wine.vintageType,
  wineDisplay: {
    producerName: wine.wineName?.producer?.name,
    regionName: wine.wineName?.region?.name,
    color: wine.wineName?.color,
  },
});

interface MenuItemFormGenericProps {
  /**
   * The intended kind in create mode (resolved by the dispatcher from the
   * ?kind query param or menu-type inference). In edit mode the loaded item's
   * kind is authoritative and this is only the initial hint.
   */
  intendedKind: MenuItemKind;
}

const MenuItemFormGeneric: React.FC<MenuItemFormGenericProps> = ({ intendedKind }) => {
  const navigate = useNavigate();
  const { menuId, itemId } = useParams<{ menuId: string; itemId?: string }>();
  const theme = useTheme();
  const { venueId, defaultCurrency } = useVenue();
  const isEdit = Boolean(itemId);
  const backPath = `/admin/venues/${venueId}/menus/${menuId}`;

  // Context: is the intended create kind WINE? (wine context forces the wine
  // identity helper on and required, preserving the old wine-form guardrail.)
  const isWineContext = intendedKind === 'WINE';
  // Only a non-wine *create* may optionally toggle the wine identity helper.
  // Wine context is always-on; edit mode is locked to the loaded kind (kind is
  // immutable, so a non-wine item can never become a wine item via edit).
  const canToggleWine = !isEdit && !isWineContext;

  // Wine identity helper — when active, the item is saved as kind=WINE.
  // Initialised on (and required) for the wine context; off for non-wine create.
  // Edit mode overwrites this once the item loads (loadedKind === 'WINE').
  const [linkWine, setLinkWine] = useState<boolean>(isWineContext);
  const wineActive = linkWine;

  // The kind whose copy/labels are shown. When the wine helper is active the
  // noun is "Wine"; otherwise the intended non-wine kind's label.
  const activeKind: MenuItemKind = wineActive ? 'WINE' : intendedKind;
  const labels = KIND_LABELS[activeKind] ?? KIND_LABELS.OTHER;

  // Wine selection — drives identity, displaySnapshot, and wineDetails.
  const [selectedWine, setSelectedWine] = useState<WineSearchResult | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createModalQuery, setCreateModalQuery] = useState('');

  // Editable form fields.
  const [formData, setFormData] = useState<FormData>(emptyFormData(defaultCurrency));
  const [errors, setErrors] = useState<Partial<Record<keyof FormData | 'wine' | 'scores', string>>>({});
  const [loading, setLoading] = useState(isEdit);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState('');

  // Sections + formats lookups.
  const [sections, setSections] = useState<MenuSection[]>([]);
  const [formats, setFormats] = useState<MenuFormat[]>([]);

  // Item-level inventory link — the single source of truth for
  // MenuItem.inventoryItemId. Loaded in edit mode and driven by
  // ItemFulfillmentPanel (MENU-EXEC.4). The per-format handler below never
  // touches this, so per-format edits can't overwrite the item-level link.
  const [linkedItem, setLinkedItem] = useState<InventoryItemWithBalance | null>(null);
  // How the placement is backed (item-level fulfillment). Create defaults to
  // linking an existing item; edit mode is set from the loaded item below.
  const [fulfillmentMode, setFulfillmentMode] = useState<FulfillmentMode>('LINK_EXISTING');
  // The per-format MenuItemFormat block is demoted behind this disclosure.
  const [showAdvancedFormats, setShowAdvancedFormats] = useState(false);

  // Wine scores rows. Stored as a UI-friendly array; empty rows dropped on save.
  const [scoreRows, setScoreRows] = useState<ScoreRow[]>([]);

  // Per-item format pricing.
  const [formatRows, setFormatRows] = useState<FormatRow[]>([]);
  const [originalFormatRows, setOriginalFormatRows] = useState<FormatRow[]>([]);

  // In edit mode we hold the originally-loaded wineDetails so update payloads
  // re-send wineDisplay verbatim (backend requires it for kind=WINE updates).
  const [originalWineDetails, setOriginalWineDetails] = useState<MenuWineDetails | null>(null);

  // ── Phase 7.A.2c — execution-item link/create support ─────────────────────
  // Human-readable item name used to pre-fill a created execution item's name.
  const menuItemName = useMemo(() => {
    if (wineActive) {
      const name = selectedWine?.name ?? originalWineDetails?.wineDisplay?.name ?? '';
      const vintage = selectedWine?.displayVintage ?? originalWineDetails?.wineDisplay?.displayVintage ?? '';
      return [name, vintage].filter(Boolean).join(' ').trim();
    }
    return formData.name.trim();
  }, [wineActive, selectedWine, originalWineDetails, formData.name]);

  // Suggested execution-item name for a serving-size row: "<item> — <format>".
  const suggestedExecutionName = useCallback(
    (formatId: string): string => {
      const f = formats.find(x => x._id === formatId);
      const label = f?.name ?? '';
      return [menuItemName, label].filter(Boolean).join(' — ');
    },
    [formats, menuItemName],
  );

  // After a link/create from the execution block, sync BOTH formatRows and
  // originalFormatRows for that row so the form's diff-save never overwrites
  // inventoryItemId with a stale value.
  const handleLinkedItemChange = useCallback(
    (formatRowId: string, item: InventoryItemWithBalance | null) => {
      setFormatRows(rows => rows.map(r => (r._id === formatRowId ? { ...r, inventoryItem: item } : r)));
      setOriginalFormatRows(rows => rows.map(r => (r._id === formatRowId ? { ...r, inventoryItem: item } : r)));
    },
    [],
  );

  // ── Load sections / formats ───────────────────────────────────────────────
  useEffect(() => {
    if (!menuId) return;
    menuSectionService.getSections(venueId, menuId)
      .then(setSections)
      .catch(() => { /* non-blocking */ });
    menuFormatService.getFormats(venueId, menuId)
      .then(setFormats)
      .catch(() => { /* non-blocking */ });
  }, [venueId, menuId]);

  // ── Load existing item in edit mode ───────────────────────────────────────
  useEffect(() => {
    if (!isEdit || !menuId || !itemId) return;
    setLoading(true);
    menuItemService.getItem(venueId, menuId, itemId)
      .then(item => {
        const itemIsWine = item.kind === 'WINE';
        // Edit mode: the loaded kind is authoritative; lock the helper to it.
        setLinkWine(itemIsWine);

        const wd = item.wineDetails;
        if (itemIsWine && wd) {
          // Seed selectedWine so WineSearchBox renders the locked label.
          setSelectedWine({
            _id: wd.wineId,
            name: wd.wineDisplay?.name ?? '(unknown)',
            displayVintage: wd.wineDisplay?.displayVintage ?? '',
            vintageType: 'single_vintage',
            wineDisplay: {
              producerName: wd.wineDisplay?.producerName,
              regionName: wd.wineDisplay?.regionName,
              color: wd.wineDisplay?.color,
            },
          });
          setOriginalWineDetails(wd);
          setScoreRows(
            Array.isArray(wd.scores)
              ? wd.scores.map(s => ({ source: s.source, score: String(s.score) }))
              : [],
          );
        }

        setFormData({
          // Name/subtitle only matter for non-wine; harmless to seed for wine.
          name: item.displaySnapshot?.name ?? '',
          subtitle: item.displaySnapshot?.subtitle ?? '',
          basePrice: item.basePrice != null ? String(item.basePrice) : '',
          currency: item.currency ?? defaultCurrency,
          sectionId: item.sectionId ?? '',
          visibility: item.visibility,
          notes: item.notes ?? '',
          externalUrl: item.externalUrl ?? '',
          needsReview: Boolean(item.needsReview),
          position: item.position != null ? String(item.position) : '',
          bottleSize: wd?.bottleSize != null ? String(wd.bottleSize) : '',
          binNumber: wd?.binNumber ?? '',
        });

        if (item.inventoryItemId) {
          // Existing item-level link → show it in "Link existing" mode.
          setFulfillmentMode('LINK_EXISTING');
          inventoryItemService.getItem(venueId, item.inventoryItemId)
            .then(inv => setLinkedItem(inv as unknown as InventoryItemWithBalance))
            .catch(() => { /* non-blocking */ });
        } else {
          // No item-level link → preserve the existing basePrice-driven
          // behavior by defaulting to manual display.
          setFulfillmentMode('MANUAL_DISPLAY');
        }
      })
      .catch(() => setLoadFailed(true))
      .finally(() => setLoading(false));
  }, [isEdit, menuId, itemId, venueId, defaultCurrency]);

  // ── Load existing per-item format pricings in edit mode ───────────────────
  useEffect(() => {
    if (!isEdit || !itemId) return;
    menuItemFormatService.getItemFormats(venueId, itemId)
      .then(async (rows: MenuItemFormatDoc[]) => {
        const mapped: FormatRow[] = await Promise.all(rows.map(async r => {
          let invItem: InventoryItemWithBalance | null = null;
          if (r.inventoryItemId) {
            try {
              invItem = (await inventoryItemService.getItem(venueId, r.inventoryItemId)) as unknown as InventoryItemWithBalance;
            } catch { /* non-blocking */ }
          }
          return {
            _id: r._id,
            formatId: typeof r.formatId === 'string' ? r.formatId : (r.formatId as any)._id,
            price: String(r.price),
            visibility: r.visibility,
            inventoryItem: invItem,
          };
        }));
        setFormatRows(mapped);
        setOriginalFormatRows(mapped.map(r => ({ ...r })));
      })
      .catch(() => { /* non-blocking */ });
  }, [isEdit, itemId, venueId]);

  // ── Bottle size lock: when the linked item has sizeValue, mirror it and
  // disable manual editing so the two sources can't disagree. (Wine only.) ──
  const bottleSizeLocked = wineActive && linkedItem?.sizeValue != null;
  useEffect(() => {
    if (wineActive && linkedItem?.sizeValue != null) {
      setFormData(prev => ({ ...prev, bottleSize: String(linkedItem.sizeValue) }));
    }
  }, [wineActive, linkedItem]);

  // ── Generic field change ──────────────────────────────────────────────────
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  // ── Score row helpers ─────────────────────────────────────────────────────
  const addScoreRow = () => {
    setScoreRows(rows => [...rows, { source: '', score: '' }]);
    setErrors(prev => ({ ...prev, scores: undefined }));
  };
  const updateScoreRow = (idx: number, patch: Partial<ScoreRow>) => {
    setScoreRows(rows => rows.map((r, i) => i === idx ? { ...r, ...patch } : r));
    setErrors(prev => ({ ...prev, scores: undefined }));
  };
  const removeScoreRow = (idx: number) => {
    setScoreRows(rows => rows.filter((_, i) => i !== idx));
    setErrors(prev => ({ ...prev, scores: undefined }));
  };
  const usedSources = (currentIdx: number): Set<MenuWineScoreSource> => {
    const set = new Set<MenuWineScoreSource>();
    scoreRows.forEach((r, i) => {
      if (i !== currentIdx && r.source) set.add(r.source);
    });
    return set;
  };

  // ── Format row helpers ────────────────────────────────────────────────────
  const addFormatRow = () => {
    setFormatRows(rows => [
      ...rows,
      { formatId: '', price: '', visibility: 'LIVE', inventoryItem: null },
    ]);
  };
  const updateFormatRow = (idx: number, patch: Partial<FormatRow>) => {
    setFormatRows(rows => rows.map((r, i) => i === idx ? { ...r, ...patch } : r));
  };
  const removeFormatRow = (idx: number) => {
    setFormatRows(rows => rows.filter((_, i) => i !== idx));
  };

  // ── Validation ────────────────────────────────────────────────────────────
  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof FormData | 'wine' | 'scores', string>> = {};

    if (wineActive) {
      if (!selectedWine) newErrors.wine = 'Pick a wine.';
    } else if (!formData.name.trim()) {
      newErrors.name = 'Name is required.';
    }

    if (formData.basePrice && (isNaN(parseFloat(formData.basePrice)) || parseFloat(formData.basePrice) < 0)) {
      newErrors.basePrice = 'Must be a non-negative number.';
    }
    if (formData.position && isNaN(parseInt(formData.position, 10))) {
      newErrors.position = 'Must be a whole number.';
    }

    if (wineActive && formData.bottleSize) {
      const bs = parseFloat(formData.bottleSize);
      if (isNaN(bs) || bs <= 0) {
        newErrors.bottleSize = 'Must be a positive number (ml).';
      }
    }

    // Scores (wine only): each row needs both source and valid score, no dups.
    if (wineActive) {
      const seenSources = new Set<string>();
      for (const row of scoreRows) {
        if (!row.source || !row.score) {
          newErrors.scores = 'Every score needs a source and a number 50–100.';
          break;
        }
        if (seenSources.has(row.source)) {
          newErrors.scores = 'Each score source can appear at most once.';
          break;
        }
        seenSources.add(row.source);
        const n = parseFloat(row.score);
        if (isNaN(n) || n < 50 || n > 100) {
          newErrors.scores = 'Scores must be numbers between 50 and 100.';
          break;
        }
      }
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return false;

    // Format pricing rows.
    const seenFormats = new Set<string>();
    for (const row of formatRows) {
      if (!row.formatId) { setApiError('Every serving size needs a format selected.'); return false; }
      if (seenFormats.has(row.formatId)) {
        setApiError('A serving size can be priced only once per item.');
        return false;
      }
      seenFormats.add(row.formatId);
      const priceNum = parseFloat(row.price);
      if (!row.price || isNaN(priceNum) || priceNum < 0) {
        setApiError('Every serving size needs a non-negative price.');
        return false;
      }
    }
    return true;
  };

  // ── Build wineDetails payload from current state (wine only) ───────────────
  const buildWineDetailsPayload = (): MenuWineDetails | null => {
    if (!selectedWine) return null;
    const bottleSize = formData.bottleSize ? parseFloat(formData.bottleSize) : undefined;
    const binNumber = formData.binNumber.trim() || undefined;
    const scores = scoreRows
      .filter(r => r.source && r.score)
      .map(r => ({ source: r.source as MenuWineScoreSource, score: parseFloat(r.score) }));

    // On edit, prefer the originally-loaded wineDisplay so we don't accidentally
    // overwrite snapshot fields the search-result projection didn't carry.
    const wineDisplay = isEdit && originalWineDetails?.wineDisplay
      ? originalWineDetails.wineDisplay
      : {
          name: selectedWine.name,
          displayVintage: selectedWine.displayVintage,
          producerName: selectedWine.wineDisplay?.producerName ?? '',
          regionName: selectedWine.wineDisplay?.regionName ?? '',
          color: selectedWine.wineDisplay?.color ?? '',
        };

    return {
      wineId: selectedWine._id,
      wineDisplay,
      ...(scores.length > 0 && { scores }),
      ...(bottleSize !== undefined && { bottleSize }),
      ...(binNumber !== undefined && { binNumber }),
    };
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiError('');
    if (!validateForm()) return;
    if (!menuId) return;

    // Build wineDetails + derived displaySnapshot when the wine helper is active.
    let wineDetails: MenuWineDetails | undefined;
    let displaySnapshot: { name: string; subtitle?: string };
    if (wineActive) {
      const wd = buildWineDetailsPayload();
      if (!wd) return;
      wineDetails = wd;
      // displaySnapshot is derived from the wine — name + producer · region.
      const subtitleBits = [wd.wineDisplay.producerName, wd.wineDisplay.regionName]
        .filter((s): s is string => Boolean(s && s.trim()));
      displaySnapshot = {
        name: wd.wineDisplay.name,
        ...(subtitleBits.length > 0 && { subtitle: subtitleBits.join(' · ') }),
      };
    } else {
      displaySnapshot = {
        name: formData.name.trim(),
        ...(formData.subtitle.trim() && { subtitle: formData.subtitle.trim() }),
      };
    }

    setSaving(true);
    try {
      const basePrice = formData.basePrice ? parseFloat(formData.basePrice) : undefined;
      const position = formData.position ? parseInt(formData.position, 10) : 0;

      let savedItemId = itemId;
      if (isEdit && itemId) {
        // Update — never send `kind` (kind is immutable post-create).
        await menuItemService.updateItem(venueId, menuId, itemId, {
          displaySnapshot,
          basePrice,
          currency: formData.currency.trim() || undefined,
          sectionId: formData.sectionId === '' ? null : formData.sectionId,
          visibility: formData.visibility,
          notes: formData.notes.trim() || undefined,
          externalUrl: formData.externalUrl.trim() || undefined,
          needsReview: formData.needsReview,
          position,
          inventoryItemId: linkedItem ? linkedItem._id : null,
          ...(wineActive && wineDetails ? { wineDetails } : {}),
        });
      } else {
        const created = await menuItemService.createItem(venueId, menuId, {
          // kind is a backend compatibility requirement (Phase G): WINE when a
          // wine identity is linked, otherwise the intended non-wine kind.
          kind: wineActive ? 'WINE' : intendedKind,
          displaySnapshot,
          basePrice,
          currency: formData.currency.trim() || undefined,
          sectionId: formData.sectionId === '' ? null : formData.sectionId,
          visibility: formData.visibility,
          source: 'MANUAL',
          notes: formData.notes.trim() || undefined,
          externalUrl: formData.externalUrl.trim() || undefined,
          needsReview: formData.needsReview,
          position,
          inventoryItemId: linkedItem ? linkedItem._id : null,
          ...(wineActive && wineDetails ? { wineDetails } : {}),
        } as Parameters<typeof menuItemService.createItem>[2]);
        savedItemId = created._id;
      }

      // ── Diff and persist per-item format pricings ─────────────────────────
      if (savedItemId) {
        const originalById = new Map(originalFormatRows.filter(r => r._id).map(r => [r._id!, r]));
        const currentIds = new Set(formatRows.filter(r => r._id).map(r => r._id!));

        for (const orig of originalFormatRows) {
          if (orig._id && !currentIds.has(orig._id)) {
            await menuItemFormatService.deleteItemFormat(venueId, savedItemId, orig._id);
          }
        }
        for (const row of formatRows) {
          const priceNum = parseFloat(row.price);
          const rowInvId = row.inventoryItem ? row.inventoryItem._id : null;
          if (!row._id) {
            await menuItemFormatService.createItemFormat(venueId, savedItemId, {
              formatId: row.formatId,
              price: priceNum,
              visibility: row.visibility,
              inventoryItemId: rowInvId,
            });
          } else {
            const orig = originalById.get(row._id);
            const origPrice = orig ? parseFloat(orig.price) : NaN;
            const origInvId = orig?.inventoryItem ? orig.inventoryItem._id : null;
            const origVisibility = orig?.visibility;
            const changed =
              origPrice !== priceNum ||
              origInvId !== rowInvId ||
              origVisibility !== row.visibility;
            if (changed) {
              await menuItemFormatService.updateItemFormat(venueId, savedItemId, row._id, {
                price: priceNum,
                visibility: row.visibility,
                inventoryItemId: rowInvId,
              });
            }
          }
        }
      }
      navigate(backPath);
    } catch (err: any) {
      const code = err?.response?.data?.code;
      const backendMessage = err?.response?.data?.message;
      switch (code) {
        case 'KIND_NOT_SUPPORTED_YET':
          setApiError(
            wineActive
              ? 'Wine items are not enabled on this menu yet.'
              : `Only ${labels.itemLabel.toLowerCase()} items can be created or edited in this phase.`,
          );
          break;
        case 'KIND_CHANGE_NOT_ALLOWED':
          setApiError('The kind of an existing item cannot be changed.');
          break;
        case 'DISPLAY_SNAPSHOT_REQUIRED':
          setErrors(prev => ({ ...prev, name: 'Name is required.' }));
          break;
        case 'WINE_DETAILS_REQUIRED':
        case 'WINE_DISPLAY_REQUIRED':
          setErrors(prev => ({ ...prev, wine: 'Wine selection and display details are required.' }));
          break;
        case 'WINE_ID_REQUIRED':
        case 'WINE_NOT_FOUND':
          setErrors(prev => ({ ...prev, wine: 'Pick a valid wine.' }));
          break;
        case 'WINE_SCORE_INVALID':
          setErrors(prev => ({ ...prev, scores: backendMessage ?? 'One of the scores is invalid (50–100 required).' }));
          break;
        case 'WINE_SCORE_DUPLICATE':
          setErrors(prev => ({ ...prev, scores: 'Each score source can appear at most once.' }));
          break;
        case 'SECTION_NOT_VALID':
          setApiError('The selected section is no longer active on this menu.');
          break;
        default:
          setApiError(backendMessage ?? 'Failed to save. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  // ── Early returns ─────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loadFailed) {
    return (
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate(backPath)} sx={{ textTransform: 'none', color: theme.palette.text.secondary, alignSelf: 'flex-start' }}>
          Menu
        </Button>
        <Alert severity="error">
          Could not load this item. It may have been removed. Go back and try again.
        </Alert>
      </Box>
    );
  }

  const titleNoun = labels.itemLabel;

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <Box sx={{ px: 3, py: 2.5, display: 'flex', alignItems: 'center', gap: 2, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}` }}>
          <Button startIcon={<BackIcon />} onClick={() => navigate(backPath)} sx={{ textTransform: 'none', color: theme.palette.text.secondary }}>
            Menu
          </Button>
          <Divider orientation="vertical" flexItem />
          <Typography variant="h6" fontWeight={600}>{isEdit ? `Edit ${titleNoun}` : `Add ${titleNoun}`}</Typography>
        </Box>

        <Box component="form" onSubmit={handleSubmit} sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {apiError && <Alert severity="error" sx={{ borderRadius: 1 }}>{apiError}</Alert>}

          {/* MENU-EXEC.3 — model explainer. A menu item is placement/display on
              this menu; what is actually sold and stock-tracked is the linked
              InventoryItem. Price and recipe live on the InventoryItem; the
              manual price below is only for items not linked to inventory. */}
          <Alert severity="info" icon={false} sx={{ borderRadius: 1, py: 0.75 }}>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 0.25 }}>
              This is a menu placement.
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              A menu item controls how something <strong>appears</strong> on this menu. What is actually{' '}
              <strong>sold and stock-tracked</strong> is the linked inventory item — that is where price,
              stock and recipe live. Link an inventory item below for stock/recipe fulfillment; the manual
              price is only used when nothing is linked.
            </Typography>
          </Alert>

          {/* Optional wine-identity toggle — only on a non-wine create. A wine
              context create is always-on (and required); edit is locked to the
              loaded kind. This is the Phase G "wine is an optional linked
              identity" affordance. */}
          {canToggleWine && (
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={linkWine}
                    onChange={e => {
                      setLinkWine(e.target.checked);
                      setErrors({});
                      setApiError('');
                    }}
                  />
                }
                label="Link a wine identity"
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                Optional. Links this item to a wine in the catalogue and saves it as a wine
                (auto-fills the display name, producer and region).
              </Typography>
            </Paper>
          )}

          {/* SECTION 1 — Identity */}
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>{labels.detailsTitle}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              {wineActive
                ? 'Pick the wine and where it appears on the menu. The display name is taken from the wine.'
                : `What the ${labels.itemLabel.toLowerCase()} is and where it appears on the menu.`}
            </Typography>
            <Stack spacing={2}>
              {wineActive ? (
                <Box>
                  <WineSearchBox
                    value={selectedWine}
                    onChange={wine => {
                      setSelectedWine(wine);
                      setErrors(prev => ({ ...prev, wine: undefined }));
                    }}
                    onCreateNew={query => {
                      setCreateModalQuery(query);
                      setCreateModalOpen(true);
                    }}
                    error={Boolean(errors.wine)}
                    helperText={
                      errors.wine
                      ?? (isEdit ? 'Wine cannot be changed in this phase.' : 'Pick an existing wine or create a new one.')
                    }
                    disabled={isEdit}
                    venueId={venueId}
                    label="Wine"
                  />
                  {selectedWine && (
                    <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 1 }}>
                      {selectedWine.wineDisplay?.producerName && (
                        <Chip size="small" label={selectedWine.wineDisplay.producerName} />
                      )}
                      {selectedWine.wineDisplay?.regionName && (
                        <Chip size="small" label={selectedWine.wineDisplay.regionName} variant="outlined" />
                      )}
                      {selectedWine.displayVintage && (
                        <Chip size="small" label={selectedWine.displayVintage} sx={{ fontFamily: 'monospace' }} />
                      )}
                      {selectedWine.wineDisplay?.color && (
                        <Chip size="small" label={selectedWine.wineDisplay.color} variant="outlined" />
                      )}
                    </Stack>
                  )}
                </Box>
              ) : (
                <>
                  <TextField
                    label={labels.itemNameLabel}
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    error={Boolean(errors.name)}
                    helperText={errors.name ?? `Required. Shown as the ${labels.itemLabel.toLowerCase()} name on the menu.`}
                    fullWidth
                    required
                    placeholder={labels.placeholder}
                    slotProps={{ htmlInput: { maxLength: 500 } }}
                  />
                  <TextField
                    label="Subtitle"
                    name="subtitle"
                    value={formData.subtitle}
                    onChange={handleInputChange}
                    fullWidth
                    placeholder="e.g. Tequila, Lime, Triple Sec"
                    helperText="Optional. Shown as a one-line description under the name."
                    slotProps={{ htmlInput: { maxLength: 500 } }}
                  />
                </>
              )}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <FormControl sx={{ minWidth: 240, flex: 1 }}>
                  <InputLabel>Section</InputLabel>
                  <Select
                    value={formData.sectionId}
                    label="Section"
                    onChange={e => setFormData(prev => ({ ...prev, sectionId: e.target.value }))}
                  >
                    <MenuItem value=""><em>Unsectioned</em></MenuItem>
                    {sections.map(s => (
                      <MenuItem key={s._id} value={s._id}>{s.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl sx={{ minWidth: 240, flex: 1 }}>
                  <InputLabel>Visibility</InputLabel>
                  <Select
                    value={formData.visibility}
                    label="Visibility"
                    onChange={e => setFormData(prev => ({ ...prev, visibility: e.target.value as MenuVisibility }))}
                  >
                    <MenuItem value="LIVE">Live — visible on menu</MenuItem>
                    <MenuItem value="HIDDEN">Hidden — not shown</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label="Position"
                  name="position"
                  value={formData.position}
                  onChange={handleInputChange}
                  error={Boolean(errors.position)}
                  helperText={errors.position ?? 'Lower = earlier'}
                  type="number"
                  sx={{ width: 120 }}
                  slotProps={{ htmlInput: { min: 0, step: 1 } }}
                />
              </Box>
            </Stack>
          </Paper>

          {/* SECTION 2 — Sellable item & price */}
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>Sellable item &amp; fulfillment</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Choose how this menu item is sold: link an existing inventory item, create a recipe-backed
              item, or keep it display-only with a manual price. Price and recipe live on the inventory item.
            </Typography>
            <Stack spacing={2.5}>
              <ItemFulfillmentPanel
                venueId={venueId}
                mode={fulfillmentMode}
                onModeChange={setFulfillmentMode}
                linkedItem={linkedItem}
                onLinkedItemChange={setLinkedItem}
                defaultCurrency={defaultCurrency}
                suggestedName={menuItemName}
              />

              {/* Manual menu price — only in display-only mode. Linked items take
                  their price from InventoryItem.salePrice. The basePrice value is
                  preserved across mode switches (only the user clears it). */}
              {fulfillmentMode === 'MANUAL_DISPLAY' && (
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <TextField
                    label={wineActive ? 'Manual bottle price' : 'Manual menu price'}
                    name="basePrice"
                    value={formData.basePrice}
                    onChange={handleInputChange}
                    error={Boolean(errors.basePrice)}
                    helperText={errors.basePrice ?? 'Shown on the menu. Not connected to stock or POS.'}
                    type="number"
                    sx={{ flex: 2, minWidth: 140 }}
                    slotProps={{ htmlInput: { min: 0, step: '0.01' } }}
                  />
                  <TextField
                    label="Currency"
                    name="currency"
                    value={formData.currency}
                    onChange={handleInputChange}
                    sx={{ width: 130 }}
                    placeholder={defaultCurrency || 'GBP'}
                    helperText={defaultCurrency ? `Default: ${defaultCurrency}` : undefined}
                    slotProps={{ htmlInput: { maxLength: 3, style: { textTransform: 'uppercase' } } }}
                  />
                </Box>
              )}

              {/* Advanced serving options — per-format MenuItemFormat rows with
                  per-serving inventory/recipe links (FormatExecutionBlock). Kept
                  fully intact but demoted behind a disclosure; the item-level
                  panel above is now the primary path. MENU-EXEC.4: per-format
                  schema/save-diff/payloads are unchanged. */}
              <Box>
                <Button
                  size="small"
                  onClick={() => setShowAdvancedFormats(v => !v)}
                  sx={{ textTransform: 'none', mb: 1 }}
                >
                  {showAdvancedFormats ? '▾ Advanced serving options' : '▸ Advanced serving options'}
                </Button>
                <Collapse in={showAdvancedFormats}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>Per-serving inventory link</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Optional/advanced. Link each serving option to a sellable inventory item so POS can
                      deduct stock or run its recipe. The item-level link above is the primary path.
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    disabled={formats.length === 0 || formatRows.length >= formats.length}
                    onClick={addFormatRow}
                    sx={{ textTransform: 'none' }}
                  >
                    Add serving option
                  </Button>
                </Box>
                {formats.length === 0 ? (
                  <Alert severity="info" sx={{ borderRadius: 1 }}>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 0.25 }}>
                      No serving options on this menu yet.
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Serving options (e.g. Bottle, 150ml glass, Plate) are an advanced way to attach
                      inventory/recipe fulfillment per size. You can still publish this item using the
                      manual price above — inventory linking can be added later.
                    </Typography>
                  </Alert>
                ) : formatRows.length === 0 ? (
                  <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    No serving options added yet. Click "Add serving option" to link one to inventory.
                  </Typography>
                ) : (
                  <Stack spacing={1.5}>
                    {formatRows.map((row, idx) => {
                      const usedElsewhere = new Set(
                        formatRows.filter((_, i) => i !== idx).map(r => r.formatId).filter(Boolean),
                      );
                      return (
                        <Paper key={idx} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                          {/* Line 1: serving size + selling price + visibility + delete */}
                          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 1.5 }}>
                            <FormControl size="small" sx={{ flex: 2, minWidth: 180 }}>
                              <InputLabel>Serving size</InputLabel>
                              <Select
                                value={row.formatId}
                                label="Serving size"
                                onChange={e => updateFormatRow(idx, { formatId: e.target.value })}
                              >
                                {formats.map(f => (
                                  <MenuItem
                                    key={f._id}
                                    value={f._id}
                                    disabled={usedElsewhere.has(f._id) && row.formatId !== f._id}
                                  >
                                    {f.name}
                                    {f.formatKind === 'VOLUME' && f.volumeMl != null && ` · ${f.volumeMl} ml`}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            <TextField
                              size="small"
                              label="Selling price"
                              value={row.price}
                              onChange={e => updateFormatRow(idx, { price: e.target.value })}
                              type="number"
                              sx={{ width: 130 }}
                              slotProps={{ htmlInput: { min: 0, step: '0.01' } }}
                            />
                            <FormControl size="small" sx={{ width: 130 }}>
                              <InputLabel>Visibility</InputLabel>
                              <Select
                                value={row.visibility}
                                label="Visibility"
                                onChange={e => updateFormatRow(idx, { visibility: e.target.value as MenuVisibility })}
                              >
                                <MenuItem value="LIVE">LIVE</MenuItem>
                                <MenuItem value="HIDDEN">HIDDEN</MenuItem>
                              </Select>
                            </FormControl>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => removeFormatRow(idx)}
                              aria-label="Remove serving size"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>

                          {/* Line 2: per-serving inventory link → sellable
                              InventoryItem (direct stock or recipe). */}
                          {row._id && menuId && itemId ? (
                            <FormatExecutionBlock
                              venueId={venueId}
                              menuId={menuId}
                              itemId={itemId}
                              formatId={row._id}
                              itemKind={activeKind}
                              suggestedName={suggestedExecutionName(row.formatId)}
                              onLinkedItemChange={handleLinkedItemChange}
                            />
                          ) : (
                            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                              Save this item first, then you can link this serving option to a sellable inventory item (stock or recipe).
                            </Typography>
                          )}
                        </Paper>
                      );
                    })}
                  </Stack>
                )}
                </Collapse>
              </Box>
            </Stack>
          </Paper>

          {/* SECTION 3 — Wine details (only when the wine identity is active) */}
          {wineActive && (
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>Wine details</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Optional bottle size, bin number and critic scores.
              </Typography>
              <Stack spacing={2.5}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <TextField
                    label="Bottle Size (ml)"
                    name="bottleSize"
                    value={formData.bottleSize}
                    onChange={handleInputChange}
                    error={Boolean(errors.bottleSize)}
                    helperText={
                      errors.bottleSize
                      ?? (bottleSizeLocked
                        ? 'Locked to linked inventory item size.'
                        : 'Optional. Defaults from linked inventory item when one is selected.')
                    }
                    disabled={bottleSizeLocked}
                    type="number"
                    sx={{ flex: 1, minWidth: 200 }}
                    slotProps={{ htmlInput: { min: 0, step: '1' } }}
                  />
                  <TextField
                    label="Bin Number"
                    name="binNumber"
                    value={formData.binNumber}
                    onChange={handleInputChange}
                    placeholder="e.g. 47"
                    helperText="Optional. Staff-only — never shown on the customer menu."
                    sx={{ flex: 1, minWidth: 200 }}
                    slotProps={{ htmlInput: { maxLength: 50 } }}
                  />
                </Box>

                {/* Scores */}
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>Scores</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Optional. 50–100, one entry per source.
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={addScoreRow}
                      disabled={scoreRows.length >= SCORE_SOURCES.length}
                      sx={{ textTransform: 'none' }}
                    >
                      Add score
                    </Button>
                  </Box>
                  {errors.scores && (
                    <Alert severity="error" sx={{ borderRadius: 1, mb: 1 }}>{errors.scores}</Alert>
                  )}
                  {scoreRows.length === 0 ? (
                    <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      No scores set.
                    </Typography>
                  ) : (
                    <Stack spacing={1.5}>
                      {scoreRows.map((row, idx) => {
                        const used = usedSources(idx);
                        return (
                          <Box key={idx} sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                            <FormControl size="small" sx={{ flex: 1, minWidth: 200 }}>
                              <InputLabel>Source</InputLabel>
                              <Select
                                value={row.source}
                                label="Source"
                                onChange={e => updateScoreRow(idx, { source: e.target.value as MenuWineScoreSource })}
                              >
                                {SCORE_SOURCES.map(src => (
                                  <MenuItem
                                    key={src}
                                    value={src}
                                    disabled={used.has(src) && row.source !== src}
                                  >
                                    {src} — {SCORE_SOURCE_LABELS[src]}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            <TextField
                              size="small"
                              label="Score"
                              value={row.score}
                              onChange={e => updateScoreRow(idx, { score: e.target.value })}
                              type="number"
                              sx={{ width: 110 }}
                              slotProps={{ htmlInput: { min: 50, max: 100, step: 1 } }}
                            />
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => removeScoreRow(idx)}
                              aria-label="Remove score"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        );
                      })}
                    </Stack>
                  )}
                </Box>
              </Stack>
            </Paper>
          )}

          {/* SECTION 4 — Links & review */}
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>Links &amp; review</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Optional. Inventory fulfillment is set per serving option above.
            </Typography>
            {/* Phase 7.C.1 — the item-level inventory link control is hidden from the
                normal operator UI. Per-serving "Stock & recipe" is now the canonical
                execution link. Any existing linkedItem (MenuItem.inventoryItemId) is
                still loaded and round-tripped in the save payload above. */}
            <Stack spacing={2}>
              <TextField
                label="External link"
                name="externalUrl"
                value={formData.externalUrl}
                onChange={handleInputChange}
                fullWidth
                placeholder="https://..."
                helperText="Optional. Shown as 'View details' on the customer view."
                slotProps={{ htmlInput: { maxLength: 2000 } }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.needsReview}
                    onChange={e => setFormData(prev => ({ ...prev, needsReview: e.target.checked }))}
                  />
                }
                label="Needs review"
              />
            </Stack>
          </Paper>

          {/* SECTION 5 — Notes */}
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>Notes</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Optional internal notes. Not shown on the customer-facing menu.
            </Typography>
            <TextField
              label="Notes"
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              fullWidth
              multiline
              rows={2}
              placeholder="Tasting notes or list copy (optional)"
              slotProps={{ htmlInput: { maxLength: 2000 } }}
            />
          </Paper>

          {/* Actions */}
          <Box sx={{ pt: 1.5, borderTop: `1px solid ${alpha(theme.palette.divider, 0.12)}`, display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
            <Button onClick={() => navigate(backPath)} sx={{ textTransform: 'none' }} disabled={saving}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={saving}
              sx={{ textTransform: 'none', fontWeight: 600, minWidth: 150 }}
            >
              {saving ? <CircularProgress size={18} color="inherit" /> : isEdit ? 'Save Changes' : 'Add to Menu'}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* CreateWineModal — opens from WineSearchBox's "+ Create new wine" sentinel */}
      <CreateWineModal
        open={createModalOpen}
        initialQuery={createModalQuery}
        onCreated={(wine: Wine) => {
          setSelectedWine(wineToSearchResult(wine));
          setErrors(prev => ({ ...prev, wine: undefined }));
          setCreateModalOpen(false);
        }}
        onClose={() => setCreateModalOpen(false)}
      />
    </Box>
  );
};

export default MenuItemFormGeneric;
